import { useCallback } from 'react';
import { useQuickFillStore } from '../../../shared/stores/useQuickFillStore';
import type { QuickFillItem } from '../../../shared/types';
import {
  downloadCSV,
  mergeCSVData,
  parseCSV,
  previewCSVImport,
  readCSVFile,
  serializeToCSV,
  type ImportPreview,
  type ImportResult,
} from '../../../shared/lib/utils/csvUtils';
import type { QuickFillMutationOperations } from './useQuickFillMutationOperations';

export type QuickFillCsvContext = Pick<QuickFillMutationOperations,
  | 'quickFillData'
  | 'properties'
  | 'snackbar'
  | 'canEditCell'
  | 'setQuickFillData'
  | 'captureSessionUndoBeforeMutation'
>;

export function useQuickFillCsvOperations(context: QuickFillCsvContext) {
  const {
    quickFillData, properties, snackbar, canEditCell, setQuickFillData,
    captureSessionUndoBeforeMutation,
  } = context;
  const extraProperties = useQuickFillStore((state) => state.extraProperties);

  const handleCSVExport = useCallback(() => {
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    if (data.length === 0) {
      snackbar.showWarning('No data to export');
      return;
    }
    const columns = ['Name', ...[...(properties || []), ...(extraProperties || [])]
      .filter((property) => property?.checked).map((property) => property.label)];
    downloadCSV(
      serializeToCSV(data, { columns, includeHeader: true }),
      `quickfill-export-${new Date().toISOString().slice(0, 10)}.csv`
    );
    snackbar.showSuccess(`Exported ${data.length} rows to CSV`);
  }, [extraProperties, properties, quickFillData, snackbar]);

  const handleCSVPreview = useCallback(async (file: File): Promise<ImportPreview> => {
    const content = await readCSVFile(file);
    return previewCSVImport(content, new Set(quickFillData.map((item) => item.Name)));
  }, [quickFillData]);

  const handleCSVImport = useCallback(async (file: File): Promise<ImportResult> => {
    const content = await readCSVFile(file);
    const { data: importedData } = parseCSV(content);
    const columns = [...(properties || []), ...(extraProperties || [])].filter(
      (property) => property?.checked
    ).map((property) => property.label);
    const { updatedData, result } = mergeCSVData(quickFillData, importedData, columns);
    const protectedData = updatedData.map((item, index) => {
      const baseline = quickFillData[index];
      if (!baseline) return item;
      const next = { ...item };
      columns.forEach((column) => {
        if (!canEditCell(baseline, column)) {
          (next as Record<string, unknown>)[column] = baseline[column as keyof QuickFillItem];
        }
      });
      return next;
    });
    if (result.updatedCount > 0) {
      captureSessionUndoBeforeMutation('CSV import');
      setQuickFillData(protectedData);
      snackbar.showSuccess(`Imported ${result.updatedCount} rows from CSV`);
    }
    return result;
  }, [
    canEditCell, captureSessionUndoBeforeMutation, extraProperties, properties,
    quickFillData, setQuickFillData, snackbar,
  ]);

  return {
    handleCSVExport,
    handleCSVPreview,
    handleCSVImport,
  };
}
